import React, {useState, useRef, FormEvent, useEffect} from 'react';
import {
  IonContent,
  IonHeader,
  IonTitle,
  IonToolbar,
  IonButton,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonItem,
  IonList,
  IonLabel,
  IonSearchbar,
  IonIcon,
  IonRow,
  IonCol,
  IonModal,
  IonButtons,
  IonPopover
} from '@ionic/react';
import {
  arrowBackOutline,
  closeOutline,
  logoAndroid,
  logoApple,
  shareOutline,
  ellipsisVertical
} from "ionicons/icons";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {
  faQuestion,
  faCircle,
  faExclamation,
  faThumbsDown,
  faHandRock,
  faThumbsUp
} from '@fortawesome/free-solid-svg-icons'

import {axiosInstance, isPWA, origin} from '../utils';
import urls from '../config/urls';
import {
  STANDARD_SIZE_QUALIFIER_ID,
  defaultSellingUnit,
  defaultSizeQualifier,
  defaultRegion,
  regionsFr,
  regionsEn
} from "../config/config";
import translations from "../config/translations";

import './Home.css';

interface StoreItemType {
  id: number,
  name: string
}

interface SellingUnitType {
  idSu: number,
  suName: string,
  total: string,
  umtCode: string
}

interface SizeQualifierType {
  id: number,
  nameSizeQualifier: string
}

interface RegionType {
  id: number,
  name: string
}

const Home: React.FC = () => {
  const searchStrRef = useRef<HTMLIonSearchbarElement>(null);
  const [storeItemId, setStoreItemId] = useState<number>(0);
  const [storeItemName, setStoreItemName] = useState<string>('');
  const [storeItems, setStoreItems] = useState<StoreItemType[]>([]);
  const [quantity, setQuantity] = useState<number>(1);
  const [sellingUnit, setSellingUnit] = useState<SellingUnitType>(defaultSellingUnit);
  const [sellingUnits, setSellingUnits] = useState<SellingUnitType[]>([]);
  const [price, setPrice] = useState<any>(null);
  const [sizeQualifier, setSizeQualifier] = useState<SizeQualifierType>(defaultSizeQualifier);
  const [sizeQualifiers, setSizeQualifiers] = useState<SizeQualifierType[]>([]);
  const [priceQualifierCode, setPriceQualifierCode] = useState<string>('NONE');
  const [shouldSuggestItems, setShouldSuggestItems] = useState<boolean>(true);
  const [shouldHelp, setShouldHelp] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(true);
  const [isQuantityValid, setIsQuantityValid] = useState<boolean>(true);
  const [isPriceValid, setIsPriceValid] = useState<boolean>(false);
  const [isPriceTouched, setIsPriceTouched] = useState<boolean>(false);
  const [region, setRegion] = useState<RegionType>(defaultRegion);
  const [shouldShowRegionPopover, setShouldShowRegionPopover] = useState<boolean>(false);
  const [shouldShowInstallationHelpFor, setShouldShowInstallationHelpFor] = useState<string>('none');
  const [lang, setLang] = useState<string>(window.location.href.includes('/epe') ? 'fr' : 'en');

  function reset() {
    if (searchStrRef && searchStrRef.current) searchStrRef.current.value = '';
    setStoreItemId(0);
    setStoreItemName('');
    setStoreItems([]);
    setQuantity(1);
    setIsQuantityValid(true);
    setSellingUnit(defaultSellingUnit);
    setSellingUnits([]);
    setPrice(null);
    setIsPriceValid(false);
    setIsPriceTouched(false);
    setSizeQualifier(defaultSizeQualifier);
    setSizeQualifiers([]);
    setPriceQualifierCode('NONE');
    setShouldSuggestItems(true);
  }

  function translate(key: string): string {
    return lang === 'fr' ? translations[key].fr : translations[key].en;
  }

  useEffect(() => {
    window.addEventListener('online', _ => {
      reset();
      setIsOnline(true);
    });
    window.addEventListener('offline', _ => setIsOnline(false));
    // This is a workaround to make a selected element scroll into view inside a select box
    // https://github.com/ionic-team/ionic/issues/19296
    // https://stackoverflow.com/questions/59951414/ionic-4-ion-select-does-not-scroll-to-selected-item-in-alert
    window.addEventListener('ionAlertDidPresent', e => {
      const selected = (e.target as HTMLElement).querySelector('[aria-checked="true"]');
      selected && selected.scrollIntoView();
    });
  }, []);

  function handleRegionClick(region: RegionType) {
    setRegion(region);
    setShouldShowRegionPopover(false);
  }

  function handleStoreItemNameChange(e: CustomEvent) {
    setPriceQualifierCode('NONE');
    if (e.detail.value.length < 3) setStoreItems([]);
    else if (shouldSuggestItems) {
      axiosInstance.get(urls.getStoreItems(e.detail.value, lang, 100))
        .then(res => setStoreItems(res.data));
    }
    if (!shouldSuggestItems) setShouldSuggestItems(true);
  }

  function handleStoreItemClick(item: StoreItemType) {
    reset();
    setStoreItemId(item.id);
    setStoreItemName(item.name);
    if (searchStrRef && searchStrRef.current) searchStrRef.current.value = item.name;
    setStoreItems([]);
    setShouldSuggestItems(false);
    axiosInstance.get(urls.getSellingUnits(item.id, lang))
      .then(res => {
        setSellingUnits(res.data);
        const popularSellingUnit = getMostPopularSellingUnit(res.data);
        setSellingUnit(popularSellingUnit);
        if (popularSellingUnit.umtCode === 'OTHER') {
          axiosInstance.get(urls.getSizeQualifiers(item.id, popularSellingUnit.idSu, lang))
            .then(res => {
              setSizeQualifiers(res.data);
              setSizeQualifier(getStandardSizeQualifier(res.data)!);
            })
        }
      });
  }

  function handleSellingUnitChange(e: CustomEvent) {
    setPriceQualifierCode('NONE');
    const previousUmtCode = sellingUnit.umtCode;
    setSellingUnit(e.detail.value);
    if (e.detail.value.umtCode !== previousUmtCode) {
      if (e.detail.value.umtCode === 'OTHER') {
        axiosInstance.get(urls.getSizeQualifiers(storeItemId, e.detail.value.idSu, lang))
          .then(res => {
            setSizeQualifiers(res.data);
            setSizeQualifier(getStandardSizeQualifier(res.data)!);
          })
      } else {
        setSizeQualifiers([]);
        setSizeQualifier(defaultSizeQualifier);
      }
    }
  }

  function handleQuantityChange(e: CustomEvent) {
    setPriceQualifierCode('NONE');
    setQuantity(e.detail.value);
    if (e.detail.value >= 0.01 && e.detail.value <= 9999.99) setIsQuantityValid(true);
    else setIsQuantityValid(false);
  }

  function handlePriceChange(e: CustomEvent) {
    setPriceQualifierCode('NONE');
    setPrice(e.detail.value);
    setIsPriceTouched(true);
    const price = parseFloat(parseFloat(e.detail.value).toFixed(2));
    if (price >= 0.01 && price <= 9999.99) setIsPriceValid(true);
    else setIsPriceValid(false);
  }

  function handlePriceBlur(e: CustomEvent) {
    const target = e.target as HTMLIonInputElement;
    if (target.value) setPrice(parseFloat(target.value.toString()).toFixed(2));
  }

  function handleSizeQualifierChange(e: CustomEvent) {
    setPriceQualifierCode('NONE');
    setSizeQualifier(e.detail.value)
  }

  function focusOnInput(inputName: string) {
    (document.getElementsByName(inputName)[0] as HTMLIonInputElement).setFocus();
  }

  function qualifyPrice(e: FormEvent) {
    e.preventDefault();
    if (storeItemId === 0) return;
    const today = new Date().toISOString().split('T')[0];
    axiosInstance.get(urls.qualifyPrice(storeItemId, price, quantity, sellingUnit.idSu, sizeQualifier.id, region.id, today))
      .then(res => setPriceQualifierCode(res.data.message))
      .catch(err => console.error(err));
  }

  function renderPriceQualifier() {
    let color;
    let faIcon;
    let shouldDoubleIcon = false;
    let titleKey;
    let messageKey;
    switch (priceQualifierCode) {
      case "UNABLE_TO_COMPUTE":
        color = 'black';
        faIcon = faCircle;
        titleKey = 'unableToComputePriceQualifierTitle';
        messageKey = 'unableToComputePriceQualifierMsg';
        break;
      case "NOT_ENOUGH_DATA":
        color = '#333';
        faIcon = faExclamation;
        titleKey = 'notEnoughDataPriceQualifierTitle';
        messageKey = 'notEnoughDataPriceQualifierMsg';
        break;
      case "BAD":
        color = '#CF0000';
        faIcon = faThumbsDown;
        titleKey = 'badPriceQualifierTitle';
        messageKey = 'badPriceQualifierMsg';
        break;
      case "AVERAGE":
        color = '#fda830';
        faIcon = faHandRock;
        titleKey = 'averagePriceQualifierTitle';
        messageKey = 'averagePriceQualifierMsg';
        break;
      case "GOOD":
        color = '#588a0f';
        faIcon = faThumbsUp;
        titleKey = 'goodPriceQualifierTitle';
        messageKey = 'goodPriceQualifierMsg';
        break;
      case "GREAT":
        color = '#588a0f';
        faIcon = faThumbsUp;
        shouldDoubleIcon = true;
        titleKey = 'greatPriceQualifierTitle';
        messageKey = 'greatPriceQualifierMsg';
        break;
      default:
        color = '#333';
        faIcon = faExclamation;
        titleKey = 'defaultPriceQualifierTitle';
        messageKey = 'defaultPriceQualifierMsg';
    }
    return (
        <IonModal isOpen={priceQualifierCode !== 'NONE'} cssClass="priceQualifierModal"
                  onDidDismiss={_ => setPriceQualifierCode('NONE')}>
          <IonToolbar>
            <IonButtons slot="end">
              <IonButton fill="clear" onClick={_ => setPriceQualifierCode('NONE')}>
                <IonIcon slot="icon-only" icon={closeOutline}/>
              </IonButton>
            </IonButtons>
          </IonToolbar>
          <h2>
            {translate(titleKey)}<br />
            <FontAwesomeIcon icon={faIcon} color={color}
                             className={faIcon === faHandRock ? 'faIcon faHandRock' : 'faIcon'}
            />
            {shouldDoubleIcon && <FontAwesomeIcon icon={faIcon} style={{color, marginLeft:5}} className='faIcon'/>}
          </h2>
          <p>{translate(messageKey)}</p>
          <IonButton className='ctaButton ctaButtonExtraMarginBottom' href={urls.getPlusP0(lang)}>
            {translate('discoverPlus')}
          </IonButton>
        </IonModal>
    )
  }

  function renderHelp() {
    return (
      <IonModal isOpen={shouldHelp} cssClass="helpModal" onDidDismiss={_ => setShouldHelp(false)}>
        <IonToolbar>
          <IonButtons slot="end">
            <IonButton fill="clear" onClick={_ => setShouldHelp(false)}>
              <IonIcon slot="icon-only" icon={closeOutline}/>
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <iframe width="100%" height="315" src={urls.getHelpVideo(lang)} frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope" allowFullScreen/>
        <IonButton className='ctaButton' href={urls.getPlusP0(lang)}>{translate('discoverPlus')}</IonButton>
      </IonModal>
    );
  }

  function getMostPopularSellingUnit(items: SellingUnitType[]) {
    return items.reduce((prev, current) => (parseInt(prev.total) >= parseInt(current.total)) ? prev : current)
  }

  function getStandardSizeQualifier(items: SizeQualifierType[]) {
    return items.find(item => item.id === STANDARD_SIZE_QUALIFIER_ID);
  }

  function isSearchInvalid() {
    return storeItemId > 0 && !!searchStrRef.current &&
      searchStrRef.current.value?.toLowerCase() !== storeItemName.toLowerCase();
  }

  function shouldDisableSubmit() {
    return !isQuantityValid || !isPriceValid || storeItemId === 0 || isSearchInvalid()
  }

  function renderRegionPopover() {
    const regions = lang === 'fr' ? regionsFr : regionsEn;
    return (
      <IonPopover isOpen={shouldShowRegionPopover} onDidDismiss={_ => setShouldShowRegionPopover(false)}>
        <IonList>
          {regions.map(region => (
            <IonItem key={region.id} onClick={_ => handleRegionClick(region)}>{region.name}</IonItem>
          ))}
        </IonList>
      </IonPopover>
    );
  }

  function renderInstallationHelpBtns() {
    return (
      <div className="installationButtonsContainer">
        <h3>{translate('install')}</h3>
        <IonButtons>
          <IonButton className="androidIcon installationButton" fill="outline"
                     onClick={_ => setShouldShowInstallationHelpFor('android')}>
            <IonIcon slot="icon-only" icon={logoAndroid}/>
          </IonButton>
          <IonButton className="appleIcon installationButton" fill="outline"
                     onClick={_ => setShouldShowInstallationHelpFor('ios')}>
            <IonIcon slot="icon-only" icon={logoApple}/>
          </IonButton>
        </IonButtons>
      </div>
    )
  }

  function renderInstallationHelp() {
    let browserName = 'Chrome';
    let installationIcon = ellipsisVertical;
    let installationIconColor = "#000";
    if (shouldShowInstallationHelpFor === 'ios') {
      browserName = 'Safari';
      installationIcon = shareOutline;
      installationIconColor= "#007AFF";
    }
    return (
      <IonModal cssClass='installationHelpModal' isOpen={shouldShowInstallationHelpFor !== 'none'}
                onDidDismiss={_ => setShouldShowInstallationHelpFor('none')}>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="end">
              <IonTitle className="installationHelpTitle">
                {`${translate('installation')} (${browserName})`}
              </IonTitle>
              <IonButton fill="clear" onClick={_ => setShouldShowInstallationHelpFor('none')}>
                <IonIcon slot="icon-only" icon={closeOutline}/>
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div className="installationHelpText">
            <p>
              {translate('installationFirstStep')}
              <IonIcon className="installationIcon" style={{color: installationIconColor}}
                       slot="icon-only" icon={installationIcon}
              />
              {shouldShowInstallationHelpFor === 'android' && <span>{translate('topRightCorner')}</span>}
            </p>
            <p>
              {shouldShowInstallationHelpFor === 'android'
                  ? translate('installationSecondStepAndroid')
                  : translate('installationSecondStepIOS')}
            </p>
            <p>
              {shouldShowInstallationHelpFor !== 'android'
                  ? translate('installationThirdStepIOS') : ''}
            </p>
          </div>
        </IonContent>
      </IonModal>
    )
  }

  function renderLogo() {
    const logoTemplate = <img alt={translate('soscLogo')} style={{height:50, marginLeft:10}}
      src="https://media.soscuisine.com/images/logos_sxscuisine_site/standard_tomate/plain_small.png"/>
    if (isPWA) return logoTemplate;
    return <a href={origin}>{logoTemplate}</a>
  }

  return (
    <>
      {isOnline && (
        <>
          <IonHeader>
            <IonToolbar>
              {!isPWA && (
                <IonRow>
                  <IonButton className="backButton" fill="clear" href={urls.getWebpage(lang)}>
                    <IonIcon className="backArrow" slot="start" icon={arrowBackOutline}/>
                    {translate('backToWeb')}
                  </IonButton>
                </IonRow>
              )}
              <IonRow className="ion-align-items-center">
                <IonCol size="3">{renderLogo()}</IonCol>
                <IonCol size="4" className="selectLang">
                  <IonSelect
                    value={lang}
                    cancelText={translate('cancel')}
                    okText={translate('ok')}
                    onIonChange={e => setLang(e.detail.value)}
                  >
                    <IonSelectOption key='en' value='en'>English</IonSelectOption>
                    <IonSelectOption key='fr' value='fr'>Français</IonSelectOption>
                  </IonSelect>
                </IonCol>
                <IonCol className="regionButton" size="2">
                  <img src={require(`../assets/img/flags/${region.id}.png`)} alt={region.name} style={{height: 26}}
                       onClick={_ => setShouldShowRegionPopover(true)}
                  />
                  {renderRegionPopover()}
                </IonCol>
                <IonCol size="2" className="helpButton">
                  <IonButton fill="clear" onClick={_ => setShouldHelp(true)}>
                    <FontAwesomeIcon icon={faQuestion} className='faQuestion'/>
                  </IonButton>
                </IonCol>
              </IonRow>
              <IonTitle className='appTitle'>{translate('homePageTitle')}</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            {!isPWA && renderInstallationHelpBtns()}
            {renderInstallationHelp()}
            {renderHelp()}
            <div className="background"/>
            <form noValidate onSubmit={qualifyPrice} onReset={reset}>
              <IonList lines="none">
                <IonItem>
                  <IonSearchbar
                    debounce={350} style={{paddingLeft:1, paddingRight:1, color: '#000'}}
                    className={isSearchInvalid() ? 'invalidSearch' : ''}
                    ref={searchStrRef}
                    placeholder={translate('itemName')}
                    onIonChange={handleStoreItemNameChange}
                  />
                </IonItem>
                {storeItems.map(elem => (
                  <IonItem className='ionItemBackground' lines="full" key={elem.id}
                           onClick={_ => handleStoreItemClick(elem)}>
                    {elem.name}
                  </IonItem>
                ))}
                {sellingUnits.length > 0 && (
                  <>
                    <IonItem className='selectContainer' onClick={_ => focusOnInput('quantity')}>
                      <IonLabel position="stacked">{translate('quantityLabel')}</IonLabel>
                      <IonInput
                        name='quantity'
                        className={isQuantityValid ? '' : 'invalid'}
                        style={{color: '#5c8ac8'}}
                        value={quantity}
                        type="number"
                        onIonChange={handleQuantityChange}
                        enterkeyhint = "next"
                      />
                    </IonItem>
                    <IonItem className='selectContainer'>
                      <IonLabel position="stacked">{translate('unitLabel')}</IonLabel>
                      <IonSelect
                        name="sellingUnit"
                        value={sellingUnit}
                        style={{color: '#5c8ac8'}}
                        interfaceOptions={{header: translate('unitInternalLabel')}}
                        cancelText={translate('cancel')}
                        okText={translate('ok')}
                        onIonChange={handleSellingUnitChange}
                      >
                        {sellingUnits.map(elem => (
                          <IonSelectOption key={elem.idSu} value={elem}>{elem.suName}</IonSelectOption>
                        ))}
                      </IonSelect>
                    </IonItem>
                    {sizeQualifiers.length > 0 && (
                      <IonItem className='selectContainer'>
                        <IonLabel position="stacked">{translate('sizeLabel')}</IonLabel>
                        <IonSelect
                          name="sizeQualifier"
                          value={sizeQualifier}
                          style={{color: '#5c8ac8'}}
                          cancelText={translate('cancel')}
                          okText={translate('ok')}
                          onIonChange={handleSizeQualifierChange}
                        >
                          {sizeQualifiers.map(elem => (
                            <IonSelectOption key={elem.id} value={elem}>{elem.nameSizeQualifier}</IonSelectOption>
                          ))}
                        </IonSelect>
                      </IonItem>
                    )}
                    <IonItem className='selectContainer' onClick={_ => focusOnInput('price')}>
                      <IonLabel position="stacked">{translate('priceLabel')}</IonLabel>
                      <IonInput
                        name='price'
                        style={{color: '#5c8ac8'}}
                        className={isPriceValid || !isPriceTouched ? '' : 'invalid'}
                        value={price}
                        type="number"
                        onIonChange={handlePriceChange}
                        onIonBlur={handlePriceBlur}
                        enterkeyhint = "go"
                      />
                    </IonItem>
                    <IonItem className="ButtonContainer">
                      {/* We used html button instead of Ionic button to work around the bug of pressing enter on mobile
                    doesn't submit the form: https://github.com/ionic-team/ionic/issues/19368 */}
                      <button className='validButton' type='submit' disabled={shouldDisableSubmit()}>
                        {translate('checkPrice')}
                      </button>
                    </IonItem>
                    <IonItem className="ButtonContainer">
                      <IonButton className='cancelButton' type='reset'>{translate('reset')}</IonButton>
                    </IonItem>
                  </>
                )}
              </IonList>
            </form>
            {renderPriceQualifier()}
          </IonContent>
        </>
      )}
      <div className="foxFamilyContainer" hidden={isOnline}>
        <img src={require('../assets/img/foxFamily.png')} alt={translate('foxFamily')}/>
        <h1>{translate('offlineMsg')}</h1>
      </div>
    </>
  );
};

export default Home;
